/**
 * Question Watermarking Utility
 * Adds visible and invisible watermarks to questions for anti-cheating
 */

/**
 * Generate watermark data for a question
 */
export const generateWatermarkData = (userId, testId, questionId, sessionId) => {
  const timestamp = Date.now();
  const watermarkString = `${userId}|${testId}|${questionId}|${sessionId}|${timestamp}`;
  
  // Create invisible watermark (base64 encoded)
  const invisibleWatermark = btoa(watermarkString).replace(/[^a-zA-Z0-9]/g, '');
  
  // Create visible watermark text
  const visibleWatermark = `${userId.substring(0, 8)}-${sessionId.substring(0, 6)}`;
  
  return {
    invisible: invisibleWatermark,
    visible: visibleWatermark,
    full: watermarkString,
    timestamp
  };
};

/**
 * Add visible watermark overlay to question container
 */
export const addVisibleWatermark = (containerElement, watermarkData, testInfo) => {
  if (!containerElement) return;
  
  // Remove existing watermark if any
  const existingWatermark = containerElement.querySelector('.question-watermark');
  if (existingWatermark) {
    existingWatermark.remove();
  }
  
  // Create watermark overlay
  const watermark = document.createElement('div');
  watermark.className = 'question-watermark';
  watermark.style.cssText = `
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(255, 0, 0, 0.1);
    color: rgba(255, 0, 0, 0.6);
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: bold;
    z-index: 1000;
    pointer-events: none;
    user-select: none;
    font-family: monospace;
    border: 1px solid rgba(255, 0, 0, 0.3);
  `;
  watermark.textContent = `ID: ${watermarkData.visible}`;
  watermark.setAttribute('data-watermark', watermarkData.invisible);
  
  // Make container position relative if not already
  const containerStyle = window.getComputedStyle(containerElement);
  if (containerStyle.position === 'static') {
    containerElement.style.position = 'relative';
  }
  
  containerElement.appendChild(watermark);
  
  // Add watermark to background pattern (harder to remove)
  addBackgroundWatermark(containerElement, watermarkData);
};

/**
 * Add invisible watermark to background (pattern-based)
 */
const addBackgroundWatermark = (containerElement, watermarkData) => {
  // Create a canvas with watermark pattern
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 200;
  canvas.style.display = 'none';
  
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'rgba(255, 0, 0, 0.02)';
  ctx.font = '8px monospace';
  ctx.fillText(watermarkData.invisible, 10, 20);
  
  // Create pattern
  const pattern = ctx.createPattern(canvas, 'repeat');
  
  // Apply as background (subtle)
  const watermarkBg = document.createElement('div');
  watermarkBg.className = 'question-watermark-bg';
  watermarkBg.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${canvas.toDataURL()});
    opacity: 0.03;
    pointer-events: none;
    z-index: 1;
  `;
  
  // Remove existing bg watermark
  const existingBg = containerElement.querySelector('.question-watermark-bg');
  if (existingBg) {
    existingBg.remove();
  }
  
  containerElement.appendChild(watermarkBg);
};

/**
 * Add watermark metadata to question element
 */
export const addWatermarkMetadata = (questionElement, watermarkData) => {
  if (!questionElement) return;
  
  // Add data attributes
  questionElement.setAttribute('data-watermark-id', watermarkData.invisible);
  questionElement.setAttribute('data-user-id', watermarkData.full.split('|')[0]);
  questionElement.setAttribute('data-session-id', watermarkData.full.split('|')[3]);
  questionElement.setAttribute('data-timestamp', watermarkData.timestamp);
  
  // Add invisible text watermark (for OCR detection)
  const invisibleText = document.createElement('span');
  invisibleText.style.cssText = `
    position: absolute;
    left: -9999px;
    opacity: 0;
    font-size: 1px;
    color: transparent;
  `;
  invisibleText.textContent = watermarkData.full;
  questionElement.appendChild(invisibleText);
};

/**
 * Extract watermark from image (for post-test analysis)
 */
export const extractWatermarkFromImage = async (imageFile) => {
  // This would be used server-side to analyze submitted photos
  // For now, return placeholder
  return {
    success: false,
    message: 'Watermark extraction requires server-side processing'
  };
};

